import { SubmitHelpers } from '@area2k/use-form';
import { useCallback } from 'react';

import Button from '@/components/Button';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import { Small } from '@/components/Typography';
import Form from '@/form';
import TextField from '@/form/TextField';
import TextAreaField from '@/form/TextAreaField';
import TextSelectField from '@/form/TextSelectField';

import {
  Certificate,
  CertificateInput,
  createCertificate,
  errorMessageFrom,
  updateCertificate,
} from './api';

// Mirrors CertificateType in graphy: CERTIFICATE = 0, BADGE = 1.
const TYPE_OPTIONS = [
  { label: 'Badge — can be required by a client', value: '1' },
  { label: 'Certificate — display only', value: '0' },
];

type FormValues = {
  name: string;
  display_name_en: string;
  display_name_es: string;
  description_en: string;
  certificate_type: string;
};

type Props = {
  certificate?: Certificate | null;
  onClose: () => void;
  onSaved: () => void;
};

const BadgeFormModal = ({ certificate, onClose, onSaved }: Props) => {
  const isEdit = !!certificate;

  const initialValues: FormValues = {
    name: certificate?.name ?? '',
    display_name_en: certificate?.display_name_en ?? '',
    display_name_es: certificate?.display_name_es ?? '',
    description_en: certificate?.description_en ?? '',
    certificate_type: certificate
      ? String(certificate.certificate_type === 'BADGE' ? 1 : 0)
      : '1',
  };

  const handleSubmit = useCallback(
    async (values: FormValues, { setFormError }: SubmitHelpers) => {
      if (!values.name || values.name.trim() === '') {
        setFormError('nameRequired', {
          title: 'Name required',
          message: 'Give the badge a name.',
          status: 'danger',
        });
        return;
      }

      const payload: CertificateInput = {
        name: values.name.trim(),
        display_name_en: values.display_name_en.trim() || values.name.trim(),
        display_name_es: values.display_name_es.trim(),
        description_en: values.description_en.trim(),
        certificate_type: Number(values.certificate_type),
      };

      try {
        if (isEdit && certificate) {
          await updateCertificate(certificate.id, payload);
        } else {
          await createCertificate(payload);
        }
        onSaved();
        onClose();
      } catch (err: any) {
        setFormError('saveFailed', {
          title: isEdit ? 'Could not save' : 'Could not create',
          message: errorMessageFrom(err, 'Please try again.'),
          status: 'danger',
        });
      }
    },
    [certificate, isEdit, onClose, onSaved],
  );

  return (
    <Modal
      disableClickout
      size="sm"
      title={isEdit ? 'Edit badge' : 'New badge'}
      onRequestClose={onClose}
    >
      <Card.Section>
        <Form initialValues={initialValues} onSubmit={handleSubmit}>
          <TextField
            autoFocus
            required
            showRequiredIndicator
            fieldId="name"
            label="Name"
            placeholder="e.g. Texas Food Handler"
          />
          <TextSelectField
            fieldId="certificate_type"
            label="Type"
            options={TYPE_OPTIONS}
          />
          <TextField
            fieldId="display_name_en"
            label="Display name (English)"
            placeholder="Defaults to the name above"
          />
          <TextField
            fieldId="display_name_es"
            label="Display name (Spanish)"
            placeholder="Shown to Spanish-speaking workers"
          />
          <TextAreaField
            fieldId="description_en"
            label="Description"
            placeholder="What this badge certifies"
          />
          <Stack vertical gap={12}>
            <Small color="light">
              Only badges can be set as a client or job requirement. Creating a
              badge does not grant it to anyone — assign it to workers from
              their profile before adding it to a client, or jobs requiring it
              will match nobody.
            </Small>
            <Stack justify="end" gap={8}>
              <Button
                a11yLabel="Cancel"
                appearance="outline"
                label="Cancel"
                type="button"
                onClick={onClose}
              />
              <Button
                a11yLabel={isEdit ? 'Save badge' : 'Create badge'}
                label={isEdit ? 'Save' : 'Create'}
                type="submit"
              />
            </Stack>
          </Stack>
        </Form>
      </Card.Section>
    </Modal>
  );
};

export default BadgeFormModal;
